import { Add, QuestionMark, TrashOutlined } from 'assets/icons/revamp';
import BaseButton from 'components/ui/BaseButton';
import Card from 'components/ui/Card';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import NumberedBadge from 'components/ui/NumberedBadge';
import COLOR from 'constants/color';
import { LogQuantityQuestion } from 'interfaces';
import { t } from 'services/i18n.service';
import { increment } from 'utils/helpers';
import {
  createDefaultLogQuantityQuestion,
  formatLogQuantityBoundValue,
  getLogQuantityMaxErrorKey,
  getLogQuantityMinErrorKey,
  hasLogQuantityRangeError,
  isLogQuantityMaxRequired,
  isLogQuantityMinRequired,
  parseLogQuantityBoundValue
} from 'utils/log-quantity-questions.util';

interface LogQuantityQuestionsEditorProps {
  questions: LogQuantityQuestion[];
  onQuestionsChange: (questions: LogQuantityQuestion[]) => void;
  onCreateQuestionId: () => string;
  compact?: boolean;
  emptyMessage?: string;
  rowKeyPrefix?: string;
}

const LogQuantityQuestionsEditor = ({
  questions,
  onQuestionsChange,
  onCreateQuestionId,
  compact = false,
  emptyMessage,
  rowKeyPrefix = ''
}: LogQuantityQuestionsEditorProps) => {
  const handleAddQuestion = () => {
    onQuestionsChange([
      ...questions,
      createDefaultLogQuantityQuestion(onCreateQuestionId())
    ]);
  };

  const updateQuestion = (id: string, patch: Partial<LogQuantityQuestion>) => {
    onQuestionsChange(
      questions.map((q) => (q.id === id ? { ...q, ...patch } : q))
    );
  };

  const titleClass = compact
    ? '~text-xs/sm font-normal'
    : '~text-sm/base font-semibold text-black dark:text-white';
  const descClass = compact
    ? '~text-[0.625rem]/xs leading-normal text-gray-600 dark:text-gray-400 ~-mt-2/3'
    : '~text-xs/sm text-gray-600 dark:text-gray-400 ~ml-0/0';

  return (
    <div className='flex flex-col ~gap-4/6'>
      <div className='flex items-start justify-between ~gap-4/6'>
        <div
          className={
            compact
              ? 'flex items-center ~gap-1/2'
              : 'flex-1 flex flex-col ~gap-1/2'
          }>
          <div className='flex items-center ~gap-2/3'>
            <QuestionMark
              styles={compact ? '~w-3/4 ~h-3/4' : '~w-4/5 ~h-4/5'}
              fill={COLOR.PRIMARY.LIGHT_900}
            />
            <h3 className={titleClass}>
              {t('tabs_revamp.log_data_tab.log_questions')}
            </h3>
          </div>
          {!compact && (
            <p className={descClass}>
              {t('tabs_revamp.log_data_tab.log_questions_description')}
            </p>
          )}
        </div>
        <BaseButton
          title={t('tabs_revamp.log_data_tab.add_question')}
          onClick={handleAddQuestion}
          variant='secondary'
          size={compact ? 'xs' : 'sm'}
          icon={
            <Add
              className='~w-3/4 h-auto'
              fill='currentColor'
              fillDarkMode='currentColor'
            />
          }
        />
      </div>
      {compact && (
        <p className={descClass}>
          {t('tabs_revamp.log_data_tab.log_questions_description')}
        </p>
      )}

      {questions.length > 0 ? (
        <div className='flex flex-col ~gap-3/6'>
          {questions.map((question, index) => {
            const minRequired = isLogQuantityMinRequired(question);
            const maxRequired = isLogQuantityMaxRequired(question);
            const rangeError = hasLogQuantityRangeError(question);
            const minErrorKey = getLogQuantityMinErrorKey(question);
            const maxErrorKey = getLogQuantityMaxErrorKey(question);
            const questionTextValid = Boolean(question.question?.trim());
            const hasBoundError = minRequired || maxRequired || rangeError;

            return (
              <Card
                size='small'
                key={`${rowKeyPrefix}${question.id}`}
                className='flex items-start ~gap-2/3'>
                <NumberedBadge
                  number={increment(index)}
                  size='xs'
                  className='flex-shrink-0 mt-1'
                />
                <div
                  className={`flex-1 flex flex-col ${!questionTextValid || hasBoundError ? '~gap-4/8' : '~gap-3/5'}`}>
                  <FloatingLabelInput
                    type='text'
                    placeholder={t(
                      'tabs_revamp.log_data_tab.enter_your_question'
                    )}
                    value={question.question}
                    onChange={(e) =>
                      updateQuestion(question.id, {
                        question: e.target.value
                      })
                    }
                    size='small'
                    showLabel
                    label=''
                    error={
                      !questionTextValid ? t('validation.empty_content') : ''
                    }
                  />
                  <div className='flex ~gap-2/4'>
                    <FloatingLabelInput
                      type='number'
                      placeholder={t('minimum')}
                      value={formatLogQuantityBoundValue(question.min_value)}
                      onChange={(e) =>
                        updateQuestion(question.id, {
                          min_value: parseLogQuantityBoundValue(e.target.value)
                        })
                      }
                      size='small'
                      showLabel
                      label={`${t('minimum')}:`}
                      error={minErrorKey ? t(minErrorKey) : ''}
                      min={0}
                    />
                    <FloatingLabelInput
                      type='number'
                      placeholder={t('maximum')}
                      value={formatLogQuantityBoundValue(question.max_value)}
                      onChange={(e) =>
                        updateQuestion(question.id, {
                          max_value: parseLogQuantityBoundValue(e.target.value)
                        })
                      }
                      size='small'
                      showLabel
                      label={`${t('maximum')}:`}
                      error={maxErrorKey ? t(maxErrorKey) : ''}
                      min={increment(question.min_value ?? 0)}
                    />
                  </div>
                </div>
                <button
                  type='button'
                  onClick={() =>
                    onQuestionsChange(
                      questions.filter((q) => q.id !== question.id)
                    )
                  }
                  className='flex-shrink-0 ~p-1.5/2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors'
                  aria-label={t('tabs_revamp.log_data_tab.delete_question')}>
                  <TrashOutlined
                    className='~w-3/5 h-auto'
                    fill={COLOR.SEMANTIC.ERROR}
                  />
                </button>
              </Card>
            );
          })}
        </div>
      ) : (
        <div
          className={
            compact
              ? ''
              : 'flex flex-col items-center justify-center ~py-8/12 ~px-4/6 text-center'
          }>
          {compact ? (
            <p className='~text-[0.625rem]/xs text-gray-600 dark:text-gray-400 m-auto'>
              {emptyMessage}
            </p>
          ) : (
            <>
              <div className='opacity-50 ~mb-2/3'>
                <QuestionMark
                  styles='~w-8/10 ~h-8/10'
                  fill={COLOR.PRIMARY.LIGHT_900}
                />
              </div>
              <p className='~text-sm/base text-gray-500 dark:text-gray-400'>
                {emptyMessage}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LogQuantityQuestionsEditor;
